import React, { useMemo } from "react";
import { SOURCE_COLORS, SOURCE_LABELS } from "./shared";
import { SkeletonChart } from "../shared/Skeleton";
import InfoTip from "../shared/InfoTip";
import { ErrorState } from "../shared/StateMessage";

const SOURCE_META = {
  ticketmaster: {
    color: SOURCE_COLORS.ticketmaster,
    label: SOURCE_LABELS.ticketmaster,
    icon: (
      <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M2 7v10a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2z" />
        <path d="M6 9v6M10 9v6M14 9v6M18 9v6" />
      </svg>
    ),
  },
  serpapi: {
    color: SOURCE_COLORS.serpapi,
    label: "Google Events",
    icon: (
      <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="11" cy="11" r="8" />
        <line x1="21" y1="21" x2="16.65" y2="16.65" />
      </svg>
    ),
  },
};

const Stat = ({ label, value, sub }) => (
  <div className="flex items-baseline justify-between gap-3 py-1.5 border-b border-gray-100 dark:border-gray-800 last:border-b-0">
    <span className="text-[11px] uppercase tracking-wider font-semibold text-gray-500 dark:text-gray-400">
      {label}
    </span>
    <span className="text-sm font-bold text-gray-900 dark:text-gray-100">
      {value}
      {sub && <span className="ml-1 text-[11px] font-normal text-gray-500 dark:text-gray-400">{sub}</span>}
    </span>
  </div>
);

const SourceCard = ({ source, data }) => {
  const meta = SOURCE_META[source] || {
    color: "#9ca3af",
    label: source,
    icon: null,
  };
  if (!data) {
    return (
      <div className="bg-white dark:bg-gray-900 border border-dashed border-gray-300 dark:border-gray-700 rounded-xl p-4 text-center text-sm text-gray-500 dark:text-gray-400">
        No {meta.label} events yet.
      </div>
    );
  }
  const events = Number(data.events || 0);
  const classified = Number(data.classified || 0);
  const avgDist = Number(data.avg_distance || 0);
  const avgScore = Number(data.avg_score || 0);
  const topScore = Number(data.top_score || 0);
  const categories = Number(data.categories || 0);
  const playbooks = Number(data.playbooks || 0);

  // Match thresholds used by CategoryDistributionPanel so the two surfaces agree.
  const unclassifiedPct = events > 0 ? Math.round(((events - classified) / events) * 1000) / 10 : 0;
  let unclassifiedLevel = null;
  if (events > 0 && classified < events) {
    if (unclassifiedPct >= 50) unclassifiedLevel = "critical";
    else if (unclassifiedPct >= 30) unclassifiedLevel = "warn";
  }
  const fullyClassified = events > 0 && classified === events;

  return (
    <div
      className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-4 flex flex-col gap-2"
      style={{ borderTop: `3px solid ${meta.color}` }}
    >
      <div className="flex items-center gap-2 mb-1">
        <span
          className="inline-flex items-center justify-center w-9 h-9 rounded-lg shrink-0"
          style={{ backgroundColor: `${meta.color}1a`, color: meta.color }}
        >
          {meta.icon}
        </span>
        <div>
          <div className="text-[10px] uppercase tracking-wider font-semibold text-gray-400 dark:text-gray-500">
            Source
          </div>
          <div className="text-sm font-bold text-gray-900 dark:text-gray-100">
            {meta.label}
          </div>
        </div>
      </div>

      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1 mb-1">
        <span className="text-2xl font-bold" style={{ color: meta.color }}>
          {events.toLocaleString()}
        </span>
        <span className="text-xs text-gray-500 dark:text-gray-400">
          event{events === 1 ? "" : "s"}
        </span>
        {unclassifiedLevel ? (
          <span className="ml-auto inline-flex items-center gap-2 max-[449px]:hidden">
            <span className="text-[10px] text-gray-400 dark:text-gray-500">
              {classified}/{events} classified
            </span>
            <span
              title={`${events - classified} of ${events} events are unclassified — classifier coverage is ${unclassifiedLevel === "critical" ? "low" : "degraded"}.`}
              className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ${
                unclassifiedLevel === "critical"
                  ? "bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-300"
                  : "bg-amber-50 text-amber-700 dark:bg-amber-900/20 dark:text-amber-300"
              }`}
            >
              <span className={`w-1.5 h-1.5 rounded-full ${unclassifiedLevel === "critical" ? "bg-red-500" : "bg-amber-500"}`} />
              {unclassifiedPct}% unclassified
            </span>
          </span>
        ) : fullyClassified ? (
          <span className="ml-auto inline-flex items-center gap-2 max-[449px]:hidden">
            <span className="text-[10px] text-gray-400 dark:text-gray-500">
              {classified}/{events} classified
            </span>
            <span
              title={`All ${events} events classified.`}
              className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium bg-emerald-50 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-300"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
              100% classified
            </span>
          </span>
        ) : (
          classified < events && (
            <span className="ml-auto text-[10px] text-gray-400 dark:text-gray-500">
              {classified}/{events} classified
            </span>
          )
        )}
      </div>

      {(unclassifiedLevel || fullyClassified) && (
        <div className="hidden max-[449px]:block mb-1">
          {(classified < events || fullyClassified) && (
            <div className="text-[10px] text-gray-400 dark:text-gray-500 mb-1">
              {classified}/{events} classified
            </div>
          )}
          {unclassifiedLevel ? (
            <div
              title={`${events - classified} of ${events} events are unclassified — classifier coverage is ${unclassifiedLevel === "critical" ? "low" : "degraded"}.`}
              className={`flex w-full items-center justify-center gap-1 rounded-full px-2 py-1 text-[11px] font-medium ${
                unclassifiedLevel === "critical"
                  ? "bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-300"
                  : "bg-amber-50 text-amber-700 dark:bg-amber-900/20 dark:text-amber-300"
              }`}
            >
              <span className={`w-1.5 h-1.5 rounded-full ${unclassifiedLevel === "critical" ? "bg-red-500" : "bg-amber-500"}`} />
              {unclassifiedPct}% unclassified
            </div>
          ) : (
            <div
              title={`All ${events} events classified.`}
              className="flex w-full items-center justify-center gap-1 rounded-full px-2 py-1 text-[11px] font-medium bg-emerald-50 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-300"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
              100% classified
            </div>
          )}
        </div>
      )}

      <div>
        <Stat label="Avg distance" value={`${avgDist.toFixed(1)} mi`} />
        <Stat label="Avg score" value={Math.round(avgScore)} />
        <Stat label="Top score" value={Math.round(topScore)} />
        <Stat label="Categories" value={categories} />
        <Stat label="Playbooks" value={playbooks} />
      </div>
    </div>
  );
};

const buildSourceInsight = (tm, sp) => {
  if (!tm && !sp) return null;
  if (!tm) return `Currently only seeing ${SOURCE_META.serpapi.label} events. Add Ticketmaster to capture ticketed concerts and pro sports.`;
  if (!sp) return `Currently only seeing Ticketmaster events. Add Google Events for local festivals, classes, and community activities.`;

  const tmDist = Number(tm.avg_distance || 0);
  const spDist = Number(sp.avg_distance || 0);
  const tmScore = Number(tm.avg_score || 0);
  const spScore = Number(sp.avg_score || 0);
  const tmTop = Number(tm.top_score || 0);
  const spTop = Number(sp.top_score || 0);

  const furtherSrc = tmDist > spDist ? "Ticketmaster" : "Google Events";
  const closerSrc = tmDist > spDist ? "Google Events" : "Ticketmaster";
  const furtherDist = Math.max(tmDist, spDist);
  const closerDist = Math.min(tmDist, spDist);
  const topSrc = spTop > tmTop ? "Google Events" : "Ticketmaster";
  const topScore = Math.max(tmTop, spTop);

  const avgHigherSrc = tmScore > spScore ? "Ticketmaster" : "Google Events";
  const higherAvg = Math.round(Math.max(tmScore, spScore));

  return `${furtherSrc} finds events further out (avg ${furtherDist.toFixed(1)} mi, ticketed concerts and sports), while ${closerSrc} surfaces local events closer to the property (avg ${closerDist.toFixed(1)} mi). ${avgHigherSrc} has a slightly higher avg opportunity score (${higherAvg}), and the single highest-scoring event overall came from ${topSrc} (top score ${topScore}). Together they give you the full picture — keep both enabled.`;
};

const SourceComparisonPanel = ({ loading, error, rows, rangeControl = null }) => {
  const { tm, sp } = useMemo(() => {
    const sourceRows = Array.isArray(rows) ? rows : [];
    return {
      tm: sourceRows.find((row) => row.source === "ticketmaster") || null,
      sp: sourceRows.find((row) => row.source === "serpapi") || null,
    };
  }, [rows]);

  const insight = useMemo(() => buildSourceInsight(tm, sp), [tm, sp]);

  return (
    <div className="bg-white dark:bg-gray-900 rounded-xl shadow border border-gray-200 dark:border-gray-800 p-5">
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3 md:gap-4 mb-4">
        <div>
          <div className="flex items-center">
            <h3 className="text-sm font-bold text-gray-800 dark:text-gray-100">Source Comparison</h3>
            <InfoTip text="Compare counts, average opportunity score, and category mix between Ticketmaster and Google. A heavy skew on either side drags down the Source Diversity score." />
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
            Ticketmaster vs Google Events — what each discovery source contributes.
          </p>
        </div>
        {rangeControl && <div className="md:shrink-0">{rangeControl}</div>}
      </div>

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <SkeletonChart height={224} className="rounded-xl" />
          <SkeletonChart height={224} className="rounded-xl" />
        </div>
      ) : error ? (
        <ErrorState message={error} />
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <SourceCard source="ticketmaster" data={tm} />
            <SourceCard source="serpapi" data={sp} />
          </div>

          {insight && (
            <div className="bg-[#32B1FF]/5 border border-[#32B1FF]/20 rounded-lg p-4 flex items-start gap-3">
              <svg className="w-5 h-5 text-[#32B1FF] shrink-0 mt-0.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 18h6M10 22h4M12 2a7 7 0 0 0-4 12.74V17a2 2 0 0 0 2 2h4a2 2 0 0 0 2-2v-2.26A7 7 0 0 0 12 2z" />
              </svg>
              <p className="text-sm text-gray-800 dark:text-gray-100 leading-relaxed">
                {insight}
              </p>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default SourceComparisonPanel;
